<template>
  <div class="contacts-section">
    <x-table
      :title="$t('ui.contacts.title')"
      :emptyStateLabel="$t('ui.contacts.empty_state_msg')"
      :columns="filteredColumns(columns)"
      :tableData="contacts"
      :enableSearch="true"
      :searchKeywords="segment.search"
      @updateSearch="updateSearch"
      :enableFilter="true"
      :filterDefinitions="getFilterDefinitions()"
      :filters="segment.filters"
      @updateFilters="updateFilters"
      selectionType="multiple"
      :totalResults="totalCount"
      :pagination="defaultPagination"
      @updatePagination="updatePagination"
      @fetchData="fetchData"
      @clickCell="clickCell"
      :showLoader="loading"
      :actions="actions"
      @emitTotalTableCount="emitTotalTableCount"
      @selectedItemsChange="selectedItemsChange"
      :tableSettingsName="tableSettingsName"
      :rowsPerPage="[5, 7, 10, 15, 25, 50]"
    ></x-table>

    <modal
      :open="deleteContactsModalOpen"
      :title="$t('ui.contacts.modals.delete.title')"
      type="warning"
      :showCancelForWarning="true"
      :saveText="$t('ui.global.delete')"
      @save="actionDelete"
      @cancel="closeDeleteContactsModal"
    >
      <p>
        {{
          $t("ui.global.delete_confirmation", {
            name: $tc("ui.contacts.enumeration", currentSelectedIds.length, { n: currentSelectedIds.length })
          })
        }}
      </p>
    </modal>
    <change-company
      :isOpen="companyChangeOpen"
      @cancel="showCompanyChange(false)"
      @save="emitId"
      :idCount="selectedIdCount"
    >
    </change-company>
  </div>
</template>

<script>
import ContactsApi from "@/api/Contacts";
import SegmentsApi from "@/api/Segments";
import ProjectsApi from "@/api/Project";
import ChangeCompany from "@/components/pages/contacts/ChangeCompany.vue";
import i18n from "@/i18n";
import XTable from "@/components/helpers/DataDisplay/XTable";
import Modal from "@/components/helpers/Layout/NewModal";
import Permission from "@/classes/Permission";

export default {
  props: {
    refresh: {
      type: [Number, Boolean],
      default: 0,
    },
    segment: {
      type: Object,
      default: () => {
        return {
          filters: {},
          search: "",
        };
      },
    },
    filtersFromParams: {
      type: [Object, Boolean],
      default: false,
    },
    mode: {
      type: String,
      default: "contacts", // contacts or segment
    },
    tableSettingsName: {
      type: String,
      default: "contacts", // contacts or segment
    },
  },
  components: { XTable, Modal, ChangeCompany },
  data() {
    return {
      contactsApi: new ContactsApi(),
      segmentsApi: new SegmentsApi(),
      projectsApi: new ProjectsApi(),
      contacts: [],
      selectedIdCount: 0,
      deleteContactsModalOpen: false,
      currentSelectedIds: [],
      searchKeywords: this.segment.search,
      searchFilters: this.segment.filters,
      loading: false,
      payload: 0,
      totalCount: 0,
      companyChangeOpen: false,
      defaultPagination: {},
      filterDefinitions: [
        {
          label: i18n.t("ui.contacts.filters.project.label"),
          attribute: "project_name",
          type: "text",
          predicate: "eq",
          include_none: i18n.t("ui.contacts.filters.project.none"),
          auto_complete: "organization-projects",
        },
        {
          label: i18n.t("ui.contacts.filters.gender.label"),
          attribute: "gender",
          type: "set",
          predicate: "eq",
          options: [
            {
              name: i18n.t("ui.contacts.filters.gender.options.male"),
              value: "male",
            },
            {
              name: i18n.t("ui.contacts.filters.gender.options.female"),
              value: "female",
            },
          ],
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
        {
          label: i18n.t("ui.contacts.filters.language.label"),
          attribute: "language",
          type: "set",
          predicate: "eq",
          options: [
            {
              name: i18n.t("ui.contacts.filters.language.options.de"),
              value: "de",
            },
            {
              name: i18n.t("ui.contacts.filters.language.options.en"),
              value: "en",
            },
          ],
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
        {
          label: i18n.t("ui.contacts.filters.invalid_email.label"),
          condition_label: i18n.t(
            "ui.contacts.filters.invalid_email.condition_label"
          ),
          attribute: "has_invalid_email",
          type: "set",
          predicate: "eq",
          options: [
            {
              name: i18n.t("ui.contacts.filters.invalid_email.options.valid"),
              value: false,
            },
            {
              name: i18n.t(
                "ui.contacts.filters.invalid_email.options.not_valid"
              ),
              value: true,
            },
          ],
        },
        {
          label: i18n.t("ui.contacts.filters.company.label"),
          attribute: "company_id",
          type: "text",
          predicate: "eq",
          include_none: i18n.t("ui.contacts.filters.company.none"),
          auto_complete: "organization-companies",
          match_by_id: true,
        },
        {
          label: i18n.t("ui.contacts.filters.created.label"),
          attribute: "created_at",
          type: "date",
        },
        {
          label: i18n.t("ui.contacts.filters.updated.label"),
          attribute: "updated_at",
          type: "date",
        },
        {
          label: i18n.t("ui.contacts.filters.city.label"),
          attribute: "city",
          type: "text",
          predicate: "eq",
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
        {
          label: i18n.t("ui.contacts.filters.country.label"),
          attribute: "country",
          type: "set",
          predicate: "eq",
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
      ],
      defaultProjectFilterDefinitions: [
        {
          label: i18n.t("ui.contacts.filters.tags.label"),
          attribute: "tags",
          type: "set",
          method: "chips",
          predicate: ["has_any", "has_all"],
          default_predicate: "has_any",
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
        {
          label: i18n.t("ui.contacts.filters.classification.label"),
          attribute: "classification",
          type: "set",
          predicate: "has_any",
          options: i18n.t("ui.contacts.filters.classification.options"),
          include_none: i18n.t("ui.contacts.filters.general.none"),
        },
      ],
      // This is a hash, because we may eventually support multiple projects in the search
      projectFilterDefinitions: {},
      actions: this.getActions(),
      columns: [
        {
          name: "contact_name",
          field: (contact) => contact.name,
          label: i18n.t("ui.contacts.columns.name"),
          required: true,
          isLink: true,
          isSticky: true,
          isBold: true,
          hideByDefault: false,
        },
        {
          name: "id",
          label: i18n.t("ui.contacts.columns.id"),
          field: (contact) => contact.id,
          credentials: "root",
          hideByDefault: Permission.isRoot() ? false : true,
        },
        // company_name is removed if plan is talent
        {
          name: "company_name",
          field: (contact) => (contact.company ? contact.company.name : ""),
          label: i18n.t("ui.contacts.columns.company"),
          isBold: true,
          isLink: true,
          hideByDefault: false,
        },
        {
          name: "created_at",
          field: (contact) => contact.created_at,
          label: i18n.t("ui.contacts.columns.created"),
          type: "dateTime",
          hideByDefault: false,
        },
        {
          name: "updated_at",
          field: (contact) => contact.updated_at,
          type: "dateTime",
          label: i18n.t("ui.contacts.columns.updated"),
          hideByDefault: false,
        },
        {
          name: "source",
          label: i18n.t("ui.contacts.columns.source"),
          field: (contact) => i18n.t("ui.contact.sources." + contact.source),
          hideByDefault: true,
        },
        {
          name: "contact_email",
          label: i18n.t("ui.contacts.columns.email"),
          type: "email",
          field: (contact) => contact.email,
          hideByDefault: false,
        },
        {
          name: "gender",
          label: i18n.t("ui.contacts.columns.gender"),
          field: (contact) => contact.gender,
          hideByDefault: true,
        },
      ],
    };
  },
  beforeMount() {
    if (!_.isEmpty(this.filtersFromParams && this.filtersFromParams.group)) {
      this.segment.filters = this.filtersFromParams;
    }
    this.addLeadSheetConditions();
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    filteredColumns(columns) {
      // filter copmany_name column if plan is talent
      if (this.$store.getters.getCurrentPlanIsTalent) {
        columns = columns.filter((column) => column.name !== "company_name");
      }
      return columns;
    },
    getActions() {
      let actions = [
        {
          name: this.$t("ui.contacts.actions.export"),
          function: this.actionExport,
        },
      ];
      // For now, these are only included when in the context of the segment page,
      // because these two actions are defined there. They should be moved into this vue.
      if (this.mode == "segment") {
        actions = actions.concat([
          {
            name: this.$t("ui.contacts.actions.send_businesskit"),
            function: this.actionSendBusinesskit,
          },
          {
            name: this.$t("ui.contacts.actions.save_to_project"),
            function: this.actionAddToProject,
          },
        ]);
      }
      if (this.mode == "contacts") {
        actions = actions.concat([
          {
            name: this.$t("ui.contacts.actions.update_company"),
            selectedOnly: true,
            class: "contact-action",
            function: this.bulkAddToCompany,
          },
          {
            name: this.$t("ui.contacts.actions.delete"),
            separator: true,
            selectedOnly: true,
            class: "contact-action-delete",
            function: this.warnActionDelete,
          },
        ]);
      }
      return actions;
    },
    updatePagination(pagination) {
      // We just respond to the updatePagination event so that we
      // get the whole default pagination, including any XTable defaults
      this.defaultPagination = pagination;
    },
    emitId(payload) {
      this.$emit("companyId", payload);
      this.companyChangeOpen = false;
    },
    async bulkAddToCompany(ids = []) {
      this.companyChangeOpen = true;
      this.currentSelectedIds = ids;
    },
    getFilterDefinitions() {
      // Add any project-specific filters as sub-menus
      // For now, we just add them directly to the filter list menu
      // But when we support multiple project filters, they can
      // be made available as fly-out menus
      let additional = [];

      Object.values(this.projectFilterDefinitions).forEach((defs) => {
        additional = additional.concat(defs);
      });

      if (additional.length > 0) {
        // Add a separator above the first one
        additional[0].separator = true;
      } else {
        additional = [
          {
            label: this.$t("ui.contacts.filters.custom_placeholder"),
            disabled: true,
            separator: true,
          },
        ];
      }

      return this.filterDefinitions.concat(additional);
    },
    showCompanyChange(bool) {
      this.companyChangeOpen = bool;
    },
    getStandardFilterAttributes() {
      return this.filterDefinitions.map((def) => {
        return def.attribute;
      });
    },
    openModalBulk() {
      this.openModalBulk = true;
    },
    convertLeadSheetToConditions(projectId, dependsOnId, leadSheet) {
      let standardFilterAttributes = this.getStandardFilterAttributes();
      let conditions = {};

      // First, add default project flter definitions
      let defaultsConditions = _.cloneDeep(
        this.defaultProjectFilterDefinitions
      );
      defaultsConditions.forEach((newCondition) => {
        newCondition.depends_on = dependsOnId;
        newCondition.static = {
          project_id: projectId,
        };
        conditions[newCondition.label] = newCondition;
      });

      // Then, add the filters defined by the project lead sheet
      Object.values(leadSheet.structure).forEach((item) => {
        let isContainer = /^container/.test(item.element_type);
        let exists = standardFilterAttributes.includes(item.name);

        if (isContainer == false && exists == false) {
          // Basic condition parameters
          let newCondition = {
            label: item.label,
            attribute: item.name,
            type: "text",
            predicate: "eq",
            include_none: this.$t("ui.contacts.filters.general.none"),
            depends_on: dependsOnId,
            static: {
              project_id: projectId,
            },
          };

          // Type-specific parameters
          if (["select", "radio", "radio_custom"].includes(item.element_type)) {
            newCondition = Object.assign(newCondition, {
              type: "set",
              options: [],
            });
            item.allowed_values.forEach((option) => {
              newCondition.options.push({
                name: option.name,
                value: option.value,
              });
            });
          } else if (["country_select"].includes(item.element_type)) {
            // For country, we don't need to include options,
            // because the country-select field already includes them
            newCondition = Object.assign(newCondition, {
              type: "set",
            });
          } else if (
            ["checkbox", "checkbox_custom"].includes(item.element_type)
          ) {
            newCondition = Object.assign(newCondition, {
              type: "set",
              predicate: "in",
              options: [],
            });
            item.allowed_values.forEach((option) => {
              newCondition.options.push({
                name: option.name,
                value: option.value,
              });
            });
          } else if (["text", "textarea"].includes(item.element_type)) {
            // No additional parameters
          } else if (
            [
              "free_drawing",
              "drawing",
              "title",
              "crawled_info",
              "readonly",
            ].includes(item.element_type)
          ) {
            // Ignore these
            newCondition = null;
          } else {
            console.warn("Unsupported attribute type", item.element_type, item);
            newCondition = null;
          }

          if (newCondition != null) {
            conditions[item.label] = newCondition;
          }
        }
      });

      // Sort by attribute label
      let ordered = {};
      Object.keys(conditions)
        .sort()
        .forEach((label) => {
          ordered[label] = conditions[label];
        });

      return Object.values(ordered);
    },
    async addLeadSheetConditions() {
      if (typeof this.searchFilters.group == "undefined") {
        return;
      }

      // Get a list of all project name conditions
      let projectConditions = [];
      this.searchFilters.group.conditions.forEach((cond) => {
        if (cond.attribute == "project_name" && cond.predicate != "has_none") {
          projectConditions[cond.value] = cond;
        }
      });

      // Find any that are new, and load their corresponding lead sheet as additional filter conditions
      for (const projectName of Object.keys(projectConditions)) {
        if (
          Object.keys(this.projectFilterDefinitions).includes(projectName) ==
          false
        ) {
          let projectCondition = projectConditions[projectName];
          let projectId = projectCondition.value_id;
          let condId = projectCondition.id;
          let leadSheet = await this.projectsApi.lead_mask(projectId);
          this.projectFilterDefinitions[
            projectName
          ] = this.convertLeadSheetToConditions(projectId, condId, leadSheet);
        }
      }

      // Drop any that are no longer included in the conditions
      let remaining = {};
      Object.keys(this.projectFilterDefinitions).forEach((name) => {
        if (Object.keys(projectConditions).includes(name)) {
          remaining[name] = this.projectFilterDefinitions[name];
        }
      });

      this.projectFilterDefinitions = remaining;
    },
    async updateFilters(filters) {
      this.searchFilters = filters;
      await this.addLeadSheetConditions();
      this.$emit("updateFilters", this.searchFilters);
    },
    updateSearch(searchkeywords) {
      this.searchKeywords = searchkeywords;
      this.$emit("updateSearch", searchkeywords);
    },
    selectedItemsChange(selectedItems) {
      this.$emit("selectedItemsChange", selectedItems);
      this.selectedIdCount = selectedItems.length;
    },
    emitTotalTableCount(totalTableCount) {
      this.$emit("emitTotalTableCount", totalTableCount);
    },
    openDeleteContactsModal() {
      this.deleteContactsModalOpen = true;
    },
    closeDeleteContactsModal() {
      this.deleteContactsModalOpen = false;
      this.currentSelectedIds = [];
    },
    warnActionDelete(ids = []) {
      this.openDeleteContactsModal();
      this.currentSelectedIds = ids;
    },
    async actionDelete() {
      let ids = this.currentSelectedIds;
      let results = await this.contactsApi.delete(ids);

      if (results.summary.num_failed == 0) {
        this.$store.commit("SET_NOTIFICATION", {
          title: "ui.segments.delete.success",
          type: "success",
        });
      } else {
        this.$store.commit("SET_NOTIFICATION", {
          title: "ui.segments.delete.error",
          type: "error",
        });
      }

      this.closeDeleteContactsModal();

      if (results.summary.num_succeeded > 0) {
        await this.fetchData();
      }
    },
    async actionExport(ids = []) {
      let response = await this.contactsApi.exportContacts({
        contact_ids: ids,
        contact_filters: this.searchFilters,
        search: this.searchKeywords,
      });

      if (response.status == 200) {
        this.$store.commit("SET_NOTIFICATION", {
          title: "ui.segments.export.success",
          type: "success",
        });
      } else {
        this.$store.commit("SET_NOTIFICATION", {
          title: "ui.segments.export.error",
          type: "error",
        });
      }
    },
    async actionSendBusinesskit(ids = []) {
      this.$emit("activateBusinessKitModal", ids);
    },
    async actionAddToProject(ids = []) {
      this.$emit("activateAddToProjectModal", ids);
    },
    async fetchData(fetchParams = {}) {
      // Build the API request
      if (fetchParams.searchKeywords) {
        this.searchKeywords = fetchParams.searchKeywords;
      }

      if (
        typeof fetchParams.searchFilters == "object" &&
        fetchParams.searchFilters
      ) {
        this.searchFilters = fetchParams.searchFilters;
      }

      let filters = _.cloneDeep(this.searchFilters);

      if (Array.isArray(_.get(filters, "group.conditions"))) {
        filters.group.conditions = filters.group.conditions.map((condition) => {
          if (condition.definition.match_by_id === true) {
            condition.value = condition.value_id;
          }

          // Remove attributes which the API does not use
          delete condition.definition;
          delete condition.id;
          delete condition.depends_on;
          delete condition.value_id;

          return condition;
        });
      }

      let request = {
        search: this.searchKeywords,
        contact_filters: filters,
        page: this.defaultPagination.page,
        per_page: this.defaultPagination.rowsPerPage,
        sort_by: this.defaultPagination.sortBy,
        sort_order: this.defaultPagination.descending ? "DESC" : "ASC",
      };

      if (typeof fetchParams.pagination == "object" && fetchParams.pagination) {
        request.per_page = fetchParams.pagination.rowsPerPage;
        request.sort_by = fetchParams.pagination.sortBy;
        request.sort_order = fetchParams.pagination.descending ? "DESC" : "ASC";
      }

      this.loading = true;

      // Make the request
      let requestResponse = null;
      try {
        requestResponse = await this.contactsApi.getAllContacts(request);
      } finally {
        this.loading = false;
        this.$emit("fetchDataComplete");
      }

      // Display both the returned contacts page and the total number of contacts
      this.contacts = requestResponse.data.resources;
      this.totalCount = requestResponse.data.total_results;
    },
    clickCell(id, fieldName) {
      if (fieldName == "contact_name") {
        this.$router.push({ path: "/contact/" + id });
      }
      if (fieldName == "company_name") {
        // Find the company ID for this contact ID
        let contact = this.contacts.find((contact) => {
          return contact.id == id;
        });
        if (
          typeof contact == "object" &&
          _.get(contact, "company.id", null) != null
        ) {
          this.$router.push({ path: "/company/" + contact.company.id });
        }
      }
    },
  },
  watch: {
    refresh: {
      handler() {
        this.fetchData();
      },
    },
  },
};
</script>

<style lang="sass">
.q-popover
  .contact-action-delete
    color: #df6854 !important
</style>



// WEBPACK FOOTER //
// Contacts.vue?b909fd06